import React, { useEffect, useState, useMemo } from "react";
import { Target, Heart, Users, AlertTriangle, BarChart3 } from "lucide-react";
import {
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Cell,
  ReferenceLine,
} from "recharts";
import { POSITION_GROUPS, POSITION_GROUP_COLORS } from "./constants";
import MetricTooltip from "./MetricTooltip";

const normPos = (p) => String(p || "").trim().toUpperCase();

const num0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};


const TeamComparisonCharts = ({ playerResults, teamAvg, isDarkMode }) => {
  const [activeView, setActiveView] = useState("trainability");

  // Group players by position category
  const positionStats = useMemo(() => {
    const stats = {};
    
    Object.entries(POSITION_GROUPS).forEach(([groupName, positions]) => {
      const groupPlayers = playerResults.filter(p => {
        const pos = normPos(p.position);
        if (pos === "GK" || pos === "GOALKEEPER") return false; // ✅ remove GK
        return positions.map(normPos).includes(pos);
      });

      
      if (groupPlayers.length > 0) {
        const avgTrainability =
        groupPlayers.reduce((sum, p) => sum + num0(p.avg_trainability_10), 0) / groupPlayers.length;

        const avgReadiness =
          groupPlayers.reduce((sum, p) => sum + num0(p.readiness_score), 0) / groupPlayers.length;

        const avgDistance =
          groupPlayers.reduce((sum, p) => sum + num0(p.total_distance_km), 0) / groupPlayers.length;
        const atRisk = groupPlayers.filter(p => p.injury_risk === "high").length;
        
        stats[groupName] = {
          name: groupName,
          players: groupPlayers,
          count: groupPlayers.length,
          avgTrainability: Number(avgTrainability.toFixed(1)),
          avgReadiness: Math.round(avgReadiness),
          avgDistance: Math.round(avgDistance * 10) / 10,
          atRisk,
          color: POSITION_GROUP_COLORS[groupName].primary,
        };
      }
    });
    
    return stats;
  }, [playerResults]);

  useEffect(() => {
    console.groupEnd();
  }, [playerResults, teamAvg]);

  // Data for bar chart comparison
  const chartData = useMemo(() => {
    return Object.values(positionStats).map(stat => ({
      position: stat.name,
      trainability: stat.avgTrainability,
      readiness: stat.avgReadiness,
      distance: stat.avgDistance,
      players: stat.count,
      atRisk: stat.atRisk,
      color: stat.color,
    }));
  }, [positionStats]);

  // Individual player comparison data
  const playerComparisonData = useMemo(() => {
    return playerResults.map(p => {
      const posGroup = Object.entries(POSITION_GROUPS).find(([_, positions]) => 
        positions.includes(p.position)
      )?.[0] || "Others";
      
      return {
        name: `${p.first_name?.[0]}. ${p.last_name}`,
        trainability: Number(num0(p.avg_trainability_10).toFixed(1)),
        readiness: p.readiness_score || 0,
        distance: p.total_distance_km || 0,
        position: posGroup,
        color: POSITION_GROUP_COLORS[posGroup]?.primary || "#6b7280",
        playerId: p.id,
      };
    }).sort((a, b) => b.trainability - a.trainability);
  }, [playerResults]);

  const teamAvgTrainability = Number(num0(teamAvg).toFixed(1));

  const views = [
    { key: "trainability", label: "Trainability", icon: Target, tooltip: "Training capacity comparison by position" },
    { key: "players", label: "Players", icon: Users, tooltip: "Individual view of all players" },
  ];

  const chartColors = isDarkMode 
    ? { grid: "#374151", text: "#9ca3af", teamAvg: "#818cf8" }
    : { grid: "#e5e7eb", text: "#6b7280", teamAvg: "#6366f1" };

  if (!playerResults || playerResults.length === 0) return null;

  

  return (
    <div className={`rounded-2xl p-5 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50/80"}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? "bg-indigo-500/20" : "bg-indigo-100"}`}>
            <BarChart3 size={18} className={isDarkMode ? "text-indigo-400" : "text-indigo-600"} />
          </div>
          <div>
            <h3 className="font-bold text-base">Team Analysis</h3>
            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              Comparison by position
            </p>
          </div>
        </div>
        
        {/* View tabs */}
        <div className="flex items-center gap-1">
          {views.map((view) => {
            const Icon = view.icon;
            const isActive = activeView === view.key;
            return (
              <MetricTooltip key={view.key} content={view.tooltip} isDarkMode={isDarkMode}>
                <button
                  onClick={() => setActiveView(view.key)}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer
                    ${isActive
                      ? isDarkMode ? "bg-indigo-600 text-white" : "bg-indigo-500 text-white"
                      : isDarkMode ? "text-gray-400 hover:text-gray-300 hover:bg-gray-700" : "text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                    }
                  `}
                >
                  <Icon size={12} />
                  {view.label}
                </button>
              </MetricTooltip>
            );
          })}
        </div>
      </div>

      {/* Position group stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {Object.values(positionStats).map((stat) => (
          <div 
            key={stat.name}
            className={`
              p-3 rounded-xl transition-all
              ${isDarkMode ? "bg-gray-900/50 hover:bg-gray-900/70" : "bg-white hover:shadow-md"}
            `}
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: stat.color }}
              />
              <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {stat.name}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDarkMode ? "bg-gray-800 text-gray-500" : "bg-gray-100 text-gray-400"}`}>
                {stat.count}
              </span>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-2xl font-bold" style={{ color: stat.color }}>
                    {stat.avgTrainability} /10
                </div>
                <div className={`text-[10px] ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}>
                  trainability /10
                </div>
              </div>
              <div className="text-right">
                <MetricTooltip content="Group average readiness" isDarkMode={isDarkMode}>
                  <div className={`text-sm font-semibold cursor-help ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                    {stat.avgReadiness}/10
                  </div>
                </MetricTooltip>
                {stat.atRisk > 0 && (
                  <MetricTooltip content={`${stat.atRisk} player(s) at high risk in this group`} isDarkMode={isDarkMode}>
                    <div className="flex items-center gap-1 text-rose-500 cursor-help">
                      <AlertTriangle size={10} />
                      <span className="text-[10px] font-medium">{stat.atRisk}</span>
                    </div>
                  </MetricTooltip>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="h-64">
        {activeView === "trainability" && (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
              <XAxis 
                dataKey="position" 
                tick={{ fontSize: 11, fill: chartColors.text }}
                axisLine={{ stroke: chartColors.grid }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={false}
                tickLine={false}
                domain={[0, 10]}
                tickFormatter={(v) => Number(v).toFixed(0)}
                label={{ value: "/10", position: "insideTopLeft", fontSize: 10, fill: chartColors.text }}
              />
              <Tooltip
                cursor={{ fill: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}
                wrapperStyle={{ outline: "none" }}
                contentStyle={{
                  backgroundColor: isDarkMode ? "#111827" : "#fff",
                  border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
                  borderRadius: "12px",
                  boxShadow: "0 10px 28px rgba(0,0,0,0.25)",
                  padding: "10px 12px",
                  fontSize: "12px",
                  color: isDarkMode ? "#e5e7eb" : "#111827",
                }}
                labelStyle={{
                  color: isDarkMode ? "#e5e7eb" : "#111827",
                  fontWeight: 800,
                  marginBottom: 6,
                }}
                itemStyle={{
                  color: isDarkMode ? "#e5e7eb" : "#111827",
                  fontWeight: 600,
                }}
                separator=": "
                labelFormatter={(label) => `Group: ${label}`}
                formatter={(value, name, props) => {
                  if (name === "trainability") {
                    // valeur en %
                  return [`${Number(value).toFixed(1)}`, "Trainability (/10)"];
                  }
                  return [value, name];
                }}
              />

              <ReferenceLine 
                y={teamAvgTrainability} 
                stroke={chartColors.teamAvg} 
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{ 
                  value: `Team: ${teamAvgTrainability}`,
                  position: "right", 
                  fontSize: 10, 
                  fill: chartColors.teamAvg 
                }}
              />
              <Bar 
                dataKey="trainability" 
                radius={[6, 6, 0, 0]}
                name="trainability"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.85} />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {activeView === "readiness" && (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
              <XAxis 
                dataKey="position" 
                tick={{ fontSize: 11, fill: chartColors.text }}
                axisLine={{ stroke: chartColors.grid }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={false}
                tickLine={false}
                domain={[0, 10]}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  fontSize: "11px"
                }}
                formatter={(value, name) => {
                  if (name === "readiness") return [`${value}%`, "Readiness"];
                  if (name === "distance") return [`${value} km`, "Avg. Distance"];
                  return [value, name];
                }}
              />
              <Bar 
                dataKey="readiness" 
                radius={[6, 6, 0, 0]}
                name="readiness"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.7} />
                ))}
              </Bar>
              <Line 
                type="monotone" 
                dataKey="distance" 
                stroke={chartColors.teamAvg} 
                strokeWidth={2}
                dot={{ fill: chartColors.teamAvg, r: 4 }}
                name="distance"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {activeView === "players" && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={playerComparisonData} 
              layout="vertical" 
              margin={{ top: 5, right: 10, left: 60, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} horizontal={false} />
              <XAxis 
                type="number"
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={{ stroke: chartColors.grid }}
                tickLine={false}
                domain={[0, 100]}
              />
              <YAxis 
                type="category"
                dataKey="name"
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={false}
                tickLine={false}
                width={55}
              />
              <Tooltip
                cursor={{ fill: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}
                contentStyle={{
                  backgroundColor: isDarkMode ? "#111827" : "#fff", // plus sombre que #1f2937
                  border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
                  borderRadius: "10px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                  fontSize: "12px",
                  color: isDarkMode ? "#e5e7eb" : "#111827", // ✅ couleur texte
                }}
                labelStyle={{
                  color: isDarkMode ? "#e5e7eb" : "#111827", // ✅ nom joueur
                  fontWeight: 700,
                }}
                itemStyle={{
                  color: isDarkMode ? "#e5e7eb" : "#111827", // ✅ ligne "Trainability"
                }}
                formatter={(value, name, props) => {
                  // value = trainability (%)
                return [`${Number(value).toFixed(1)}`, `Trainability (/10) (${props.payload.position})`];
                }}
              />

              <ReferenceLine 
                x={teamAvgTrainability} 
                stroke={chartColors.teamAvg} 
                strokeDasharray="5 5"
                strokeWidth={2}
              />
              <Bar 
                dataKey="trainability" 
                radius={[0, 4, 4, 0]}
              >
                {playerComparisonData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color} 
                    fillOpacity={entry.trainability >= teamAvgTrainability ? 0.9 : 0.5} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend */}
      <div className={`mt-4 pt-3 border-t flex items-center justify-center gap-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
        {Object.entries(POSITION_GROUP_COLORS).map(([name, colors]) => (
          <div key={name} className="flex items-center gap-1.5">
            <div 
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: colors.primary }}
            />
            <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{name}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div 
            className="w-4 h-0.5"
            style={{ backgroundColor: chartColors.teamAvg }}
          />
          <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Team Average</span>
        </div>
      </div>
    </div>
  );
};

export default TeamComparisonCharts;
